import { inject, Injectable } from '@angular/core';

import { catchError, map, Observable, of } from 'rxjs';

import { MpsApiService } from '../../mps/services/mps-api.service';
import { applicationEndpoints } from '../application-endpoints';
import { LaravelDefaultResponse } from '../interfaces/responses';
import { EMPTY_PROFILE, Profile } from '../models/profile.model';
import { Subsidiary } from '../models/subsidiary.model';

const PROFILE_KEY = 'oser_user';
const SUBSIDIARIES_CACHE_KEY = 'oser_subsidiaries';

@Injectable({ providedIn: 'root' })
export class ProfileService {
  private apiService = inject(MpsApiService);

  /**
   * Charge les filiales depuis l'API publique. En cas d'échec (offline), retombe
   * sur le cache local si présent.
   */
  loadSubsidiaries(): Observable<Subsidiary[]> {
    return this.apiService.get<LaravelDefaultResponse<Subsidiary[]>>(applicationEndpoints.publicSubsidiaries).pipe(
      map((response) => {
        localStorage.setItem(SUBSIDIARIES_CACHE_KEY, JSON.stringify(response.data));
        return response.data;
      }),
      catchError(() => of(this.cachedSubsidiaries()))
    );
  }

  private cachedSubsidiaries(): Subsidiary[] {
    const raw = localStorage.getItem(SUBSIDIARIES_CACHE_KEY);
    return raw ? (JSON.parse(raw) as Subsidiary[]) : [];
  }

  getProfile(): Profile {
    const raw = localStorage.getItem(PROFILE_KEY);
    return raw ? { ...EMPTY_PROFILE, ...(JSON.parse(raw) as Partial<Profile>) } : { ...EMPTY_PROFILE };
  }

  saveProfile(profile: Profile): void {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  }

  /**
   * Un profil est complet si tous les champs requis sont renseignés (phone optionnel).
   * Équivalent du resolver `needProfile` de l'ancien OSER.
   */
  isComplete(profile: Profile = this.getProfile()): boolean {
    return Boolean(
      profile.subsidiary_id && profile.person_status && profile.firstname && profile.lastname && profile.email
    );
  }
}
